import CollectionService from '@boServices/collectionService';
import { useCallback, useEffect, useRef, useState } from 'react';

const collectionService = new CollectionService();

// Ids rebuilt from a comma-separated key.
const idsOf = (key) => (key ? key.split(',') : []);

const useCollections = ({ search, page, perPage, domainTagIds = [], clientIds = [], standard = false, sortBy }) => {
    // The caller rebuilds the arrays on every render; depend on their content instead
    // of their identity, and rebuild them from those keys inside the effect.
    const domainTagIdsKey = domainTagIds.join(',');
    const clientIdsKey = clientIds.join(',');
    const [collections, setCollections] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const abortRef = useRef(null);

    const fetchCollections = useCallback(() => {
        // A newer request cancels the previous one, whose late result must not overwrite the current list.
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        setLoading(true);
        return collectionService
            .getCollections({
                search,
                page,
                perPage,
                domainTagIds: idsOf(domainTagIdsKey),
                clientIds: idsOf(clientIdsKey),
                standard,
                sortBy,
                signal: controller.signal,
            })
            .then((data) => {
                if (controller.signal.aborted) return;
                setCollections(data.items ?? []);
                setTotal(data.total ?? 0);
                setError(null);
            })
            .catch((err) => {
                if (!controller.signal.aborted) setError(err);
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });
    }, [search, page, perPage, domainTagIdsKey, clientIdsKey, standard, sortBy]);

    useEffect(() => {
        fetchCollections();
        return () => abortRef.current?.abort();
    }, [fetchCollections]);

    return { collections, total, loading, error, reload: fetchCollections };
};

export default useCollections;
